import { useState, useCallback } from 'react';
import axios from 'axios';
import { getConfig } from '../config';

/**
 * Custom hook for managing Google OAuth flow
 *
 * Handles:
 * - Initiating OAuth flow with backend
 * - Storing state token in sessionStorage for CSRF protection
 * - Tracking flow type (waitlist/login/link)
 * - Managing loading and error states
 *
 * @returns {Object} OAuth hook API
 * @returns {Function} initiateOAuth - Start OAuth flow for given type
 * @returns {boolean} isLoading - True during OAuth initialization
 * @returns {string|null} error - Error message if OAuth initiation fails
 */
export const useGoogleOAuth = () => {
  const config = getConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initiate Google OAuth flow
   *
   * @param {string} flowType - 'waitlist' | 'login' | 'link'
   */
  const initiateOAuth = useCallback(async (flowType, intent = null) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Get Google auth URL from backend with callback redirect URI
      const redirectUri = `${window.location.origin}/oauth/callback`;
      const params = { redirect_uri: redirectUri };

      // Add intent parameter if provided (for consent screen control)
      if (intent) {
        params.intent = intent;
      }

      const response = await axios.get(
        `${config.apiBaseUrl}/auth/google/authorize`,
        { params }
      );

      // 2. Get state token from backend response
      const state = response.data.state;
      const authUrl = response.data.auth_url;

      if (!state || !authUrl) {
        throw new Error('No state token or auth URL received from backend');
      }

      // 3. Store state and flow type in sessionStorage (T008)
      // These survive the redirect to Google and back
      sessionStorage.setItem('google_oauth_state', state);
      sessionStorage.setItem('google_oauth_flow', flowType);
      sessionStorage.setItem('google_oauth_initiated_at', Date.now().toString());

      // 4. Redirect to Google OAuth consent screen
      // User will be redirected back to /oauth/callback after consent
      window.location.href = authUrl;
    } catch (err) {
      const errorMessage = err.response?.data?.detail ||
                          err.message ||
                          'Failed to initiate Google login';
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [config.apiBaseUrl]);

  return {
    initiateOAuth,
    isLoading,
    error
  };
};
